import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { interviewService, responseService } from "../services";
import { FiArrowLeft, FiArrowRight, FiX, FiCheck } from "react-icons/fi";

const MicrophoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="mic-icon"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
    />
  </svg>
);

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${secs}`;
};

const InterviewSession = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Estados del formulario
  const [interview, setInterview] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Estados del reconocimiento de voz
  const [isListening, setIsListening] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [userAnswer, setUserAnswer] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("");
  const [totalTime, setTotalTime] = useState(0);

  // Referencias
  const recognitionRef = useRef(null);
  const userAnswerRef = useRef("");
  const totalTimeIntervalRef = useRef(null);
  const questionStartTimeRef = useRef(Date.now());
  const questionDurationsRef = useRef({});

  useEffect(() => {
    fetchInterview();
    // eslint-disable-next-line
  }, [interviewId]);

  // Inicializar reconocimiento de voz
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "es-ES";

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        if (finalTranscript) {
          setUserAnswer((prev) => {
            const newAnswer = prev + finalTranscript;
            userAnswerRef.current = newAnswer;
            return newAnswer;
          });
        }
        if (interimTranscript)
          setVoiceStatus(`Escuchando: ${interimTranscript}`);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        if (userAnswerRef.current.trim()) {
          setIsConfirming(true);
          setVoiceStatus("Revisa tu respuesta y confirma o reintenta.");
        } else {
          setVoiceStatus("Haz clic en el micrófono para intentarlo de nuevo.");
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setVoiceStatus("Error de reconocimiento de voz. Revisa tu micrófono.");
        setIsListening(false);
        toast.error("Error de reconocimiento de voz.");
      };
    } else {
      setVoiceStatus(
        "El reconocimiento de voz no es compatible con tu navegador."
      );
      toast.error("Reconocimiento de voz no soportado.");
    }

    // Temporizador total que corre mientras la entrevista esté en progreso
    if (interview && interview.status === "in_progress") {
      totalTimeIntervalRef.current = window.setInterval(() => {
        setTotalTime((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (totalTimeIntervalRef.current)
        clearInterval(totalTimeIntervalRef.current);
    };
  }, [interview]);

  // Temporizador de respuesta
  useEffect(() => {
    let answerTimer;
    if (isListening) {
      answerTimer = window.setInterval(() => {
        // Timer running for voice recording
      }, 1000);
    }
    return () => clearInterval(answerTimer);
  }, [isListening]);

  // Rastrear tiempo de cada pregunta
  useEffect(() => {
    // Cuando cambias de pregunta, guarda el tiempo invertido en la pregunta anterior
    if (currentQuestion > 0) {
      const timeSpent = Math.round((Date.now() - questionStartTimeRef.current) / 1000);
      questionDurationsRef.current[currentQuestion - 1] = timeSpent;
    }
    // Reinicia el tiempo para la nueva pregunta
    questionStartTimeRef.current = Date.now();
  }, [currentQuestion]);

  const fetchInterview = async () => {
    try {
      setLoading(true);
      const response = await interviewService.getInterview(interviewId);
      const interviewData = response.data.interview;
      setInterview(interviewData);

      if (interviewData.status === "in_progress") {
        const map = {};
        (interviewData.questions || []).forEach((q, idx) => {
          if (q?.responses?.[0]?.responseText)
            map[idx] = q.responses[0].responseText;
        });
        setResponses(map);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading interview");
      navigate("/interviews");
    } finally {
      setLoading(false);
    }
  };

  const isCompleted = interview && interview.status === "completed";
  const isInProgress = interview && interview.status === "in_progress";
  const questions = interview?.questions || [];
  const question = questions[currentQuestion] || {};

  const responseSaved = question?.responses?.[0]?.responseText || "";
  const localResponse = responses[currentQuestion] || "";

  const allAnswered = questions.every((q, idx) => {
    if (isCompleted) return q?.responses?.[0]?.responseText?.trim().length > 0;
    const saved = q?.responses?.[0]?.responseText;
    const temp = responses[idx];
    return (
      (temp && temp.trim().length > 0) || (saved && saved.trim().length > 0)
    );
  });

  const handleResponseChange = (e) => {
    setResponses((prev) => ({
      ...prev,
      [currentQuestion]: e.target.value,
    }));
  };

  const handleSaveResponse = async () => {
    const resp = (responses[currentQuestion] || "").trim();
    if (!resp) {
      toast.warning("Introduce una respuesta antes de continuar");
      return;
    }
    try {
      setSubmitting(true);
      const questionId = question._id;
      
      // Calcular el tiempo invertido en esta pregunta
      const questionDuration = Math.round((Date.now() - questionStartTimeRef.current) / 1000);
      
      await responseService.submitResponse({
        questionId,
        interviewId,
        responseText: resp,
        duration: questionDuration, // Enviar duración en segundos
      });
      toast.success("Respuesta guardada");
      await fetchInterview();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error al guardar respuesta"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteInterview = async () => {
    try {
      setSubmitting(true);
      await interviewService.updateInterviewStatus(interviewId, {
        status: "completed",
        duration: totalTime, // Enviar el tiempo total en segundos
      });
      toast.success("¡Entrevista completada!");
      setTimeout(() => navigate("/interviews"), 1200);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error al completar la entrevista"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleListenToggle = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else if (recognitionRef.current) {
      setUserAnswer("");
      userAnswerRef.current = "";
      recognitionRef.current.start();
      setIsListening(true);
      setVoiceStatus("Escuchando...");
    }
  };

  const handleConfirmAnswer = () => {
    if (!userAnswer.trim()) return;
    setResponses((prev) => ({
      ...prev,
      [currentQuestion]: userAnswer.trim(),
    }));
    setIsConfirming(false);
    setUserAnswer("");
    userAnswerRef.current = "";
    setVoiceStatus("Respuesta confirmada. Procede al siguiente paso.");
  };

  const handleRetryAnswer = () => {
    setIsConfirming(false);
    setUserAnswer("");
    userAnswerRef.current = "";
    setVoiceStatus("Puedes empezar a grabar de nuevo cuando quieras.");
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (loading) {
    return (
      <div className="interview-loading-container">
        <div className="interview-loading-spinner"></div>
      </div>
    );
  }
  if (!interview || !questions.length) {
    return (
      <div className="interview-empty-state">{t("interview.noInterviews")}</div>
    );
  }

  return (
    <div className="interview-session-container">
      <div className="interview-session-content">
        <div className="interview-header">
          <div className="interview-header-top">
            <h1 className="interview-title">{interview?.title}</h1>
            <span className="interview-progress-text">
              {currentQuestion + 1} / {questions.length}
            </span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill progress-bar-fill-dynamic"
              style={{
                '--progress-width': `${progress}%`,
              }}
            ></div>
          </div>
        </div>

        <div className="interview-question-card">
          <div className="interview-question-header">
            <div className="interview-question-number">
              {currentQuestion + 1}
            </div>
            <p className="interview-question-meta">
              Difficulty: {question?.difficulty || "unknown"}
            </p>
          </div>

          <h2 className="interview-question-title">
            {question?.questionText ||
              question?.question ||
              "Question not found"}
          </h2>

          <div className="interview-answer-section">
            <label className="interview-answer-label">
              {t("interview.answer")}
            </label>

            {isCompleted ? (
              <div className="interview-answer-display">
                {responseSaved ? (
                  responseSaved
                ) : (
                  <span className="interview-answer-placeholder">
                    {t("interview.noResponse")}
                  </span>
                )}
              </div>
            ) : isInProgress ? (
              <div>
                {/* Área de entrada con reconocimiento de voz o textarea */}
                {isConfirming ? (
                  <div className="interview-confirmation-box">
                    <p className="interview-confirmation-title">
                      Tu respuesta (Pendiente de confirmación):
                    </p>
                    <p className="italic">{userAnswer}</p>
                  </div>
                ) : (
                  <textarea
                    value={localResponse || userAnswer}
                    onChange={handleResponseChange}
                    disabled={isListening || submitting}
                    className="interview-textarea"
                    rows="6"
                    placeholder={t("Pregunta sin responder.")}
                  />
                )}

                {/* Estado del reconocimiento de voz */}
                {voiceStatus && (
                  <div className="interview-voice-status">{voiceStatus}</div>
                )}

                {/* Tiempos - mostrar siempre durante la entrevista en progreso */}
                {isInProgress && (
                  <div className="interview-timer-info">
                    <span>Tiempo Total: {formatTime(totalTime)}</span>
                  </div>
                )}

                {/* Botones de acción */}
                <div className="interview-voice-controls">
                  {isConfirming ? (
                    <>
                      <button
                        onClick={handleRetryAnswer}
                        className="interview-voice-button-retry"
                      >
                        Reintenta Respuesta
                      </button>
                      <button
                        onClick={handleConfirmAnswer}
                        className="interview-voice-button-confirm"
                      >
                        Confirmar Respuesta
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleListenToggle}
                        disabled={submitting}
                        className={`interview-mic-button ${
                          isListening ? "listening" : ""
                        }`}
                      >
                        <MicrophoneIcon />
                        {isListening ? "Detener" : "Grabar"}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveResponse}
                        disabled={
                          submitting || localResponse.trim().length === 0
                        }
                        className="interview-voice-button-start"
                      >
                        Guardar respuesta
                      </button>
                    </>
                  )}
                </div>

                <p className="interview-helper-text">
                  {!isConfirming &&
                    (isListening
                      ? "Haz clic en el micrófono para detener"
                      : "Haz clic en el micrófono para grabar tu respuesta")}
                </p>
              </div>
            ) : null}
          </div>

          <div className="interview-nav-controls">
            {currentQuestion > 0 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                className="interview-nav-button-prev"
              >
                <FiArrowLeft /> {t("interview.previousQuestion")}
              </button>
            )}
            {currentQuestion < questions.length - 1 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                className="interview-nav-button-skip"
              >
                {t("interview.nextQuestion")} <FiArrowRight />
              </button>
            )}
            <div className="interview-nav-spacer"></div>
            {currentQuestion === questions.length - 1 ? (
              isCompleted ? (
                <button
                  onClick={() => navigate("/interviews")}
                  className="interview-nav-button-next"
                >
                  <FiX /> Salir
                </button>
              ) : isInProgress && allAnswered ? (
                <button
                  onClick={handleCompleteInterview}
                  disabled={submitting}
                  className="interview-nav-button-submit"
                >
                  <FiCheck /> Completar entrevista
                </button>
              ) : isInProgress ? (
                <button
                  onClick={() => navigate("/interviews")}
                  className="interview-nav-button-next"
                >
                  <FiX /> Salir
                </button>
              ) : null
            ) : (
              <button
                onClick={() => navigate("/interviews")}
                className="interview-nav-button-finish"
              >
                <FiX /> Salir
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewSession;
